import React, { useState, useContext, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Alert, 
  CircularProgress, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { HttpServiceContext } from '../state/HttpProvider';
import { useAppStateContext } from '../state/AppContextProvider';
import { AccessContext } from '../state/AccessProvider';
import { useOAuth } from '../auth/OAuthProvider';

interface OrganizationMember {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  joinedAt: string;
}

interface SubscriptionDetails {
  type: 'personal' | 'enterprise';
  organization?: {
    id: string;
    name: string;
    email: string;
    subscriptionPlan: string;
    isActive: boolean;
  };
  seats?: {
    total: number;
    used: number;
    available: number;
  };
  members?: OrganizationMember[];
}

export const SubscriptionManager: React.FC = () => {
  const { httpService } = useContext(HttpServiceContext);
  const { subscriptionVerified, refreshSubscriptionStatus } = useAppStateContext();
  const { user: oauthUser } = useOAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  
  // Check if user is subscription owner using oauthUser from useOAuth hook
  const isSubscriptionOwner = oauthUser?.roles?.some((role: string) => 
    role.toLowerCase() === 'subscription-owner'
  ) ?? false;

  // Load subscription details including members for enterprise
  const loadSubscriptionDetails = async () => {
    try {
      const details = await httpService.getOrganizationDetails();
      setSubscriptionDetails(details);
    } catch (err: any) {
      console.error('Failed to load subscription details:', err);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail) return;
    
    setInviteLoading(true);
    setError(null);
    setMessage(null);

    try {
      await httpService.inviteOrganizationMember(inviteEmail);
      setMessage(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteDialogOpen(false);
      await loadSubscriptionDetails();
    } catch (err: any) {
      setError(err.message || 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemoveMember = async (memberId: string, memberEmail: string) => {
    if (!window.confirm(`Are you sure you want to remove ${memberEmail} from the organization?`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      await httpService.removeOrganizationMember(memberId);
      setMessage(`${memberEmail} has been removed`);
      await loadSubscriptionDetails();
    } catch (err: any) {
      setError(err.message || 'Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const handleForfeitSeat = async () => {
    if (!window.confirm('Are you sure you want to forfeit your seat? You will lose access to the application.')) {
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      await httpService.forfeitSeat();
      setMessage('Your seat has been forfeited. You will lose access shortly.');
      await refreshSubscriptionStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to forfeit seat');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.')) {
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const response = await httpService.cancelSubscription();
      setMessage(response.message);
      await refreshSubscriptionStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel subscription');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshSubscriptionStatus();
    if (subscriptionVerified) {
      loadSubscriptionDetails();
    }
  }, [subscriptionVerified]);

  if (subscriptionVerified === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!subscriptionVerified) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">
          You don't have an active subscription.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Subscription Management
      </Typography>
      
      <Alert severity="success" sx={{ mb: 2 }}>
        Your subscription is active and verified.
        {subscriptionDetails?.type === 'enterprise' && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Plan: <strong>Enterprise ({subscriptionDetails.seats?.total} seats)</strong>
          </Typography>
        )}
        {subscriptionDetails?.type === 'personal' && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Plan: <strong>Personal</strong>
          </Typography>
        )}
      </Alert>

      {message && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Enterprise Seat Management - Only for subscription owners */}
      {isSubscriptionOwner && subscriptionDetails?.type === 'enterprise' && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Team Members
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setInviteDialogOpen(true)}
              disabled={subscriptionDetails.seats!.available === 0}
              sx={{
                background: 'linear-gradient(135deg, #e50c20 0%, #c00a1a 100%)',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #c00a1a 0%, #a00818 100%)',
                  boxShadow: '0 4px 12px rgba(229, 12, 32, 0.4)',
                },
                '&:disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                }
              }}
            >
              Invite Member
            </Button>
          </Box>

          <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
            <Chip 
              label={`${subscriptionDetails.seats!.used} / ${subscriptionDetails.seats!.total} Seats Used`}
              color="primary"
              variant="outlined"
            />
            <Chip 
              label={`${subscriptionDetails.seats!.available} Available`}
              color="success"
              variant="outlined"
            />
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptionDetails.members?.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>{member.name}</TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={member.status} 
                        size="small"
                        color={member.status === 'active' ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(member.joinedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      {member.email !== oauthUser?.email && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveMember(member.id, member.email)}
                          disabled={loading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Subscription Actions */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Subscription Actions
        </Typography>
        
        {isSubscriptionOwner ? (
          // Owner can cancel subscription
          <>
            <Button
              variant="outlined"
              color="error"
              onClick={handleCancelSubscription}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Cancelling...' : 'Cancel Subscription'}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Cancelling your subscription will remove access for all team members at the end of the billing period.
            </Typography>
          </>
        ) : (
          // Non-owners can only forfeit their seat
          <>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleForfeitSeat}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Forfeiting...' : 'Forfeit My Seat'}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Forfeiting your seat will remove your access. Contact your organization owner to cancel the subscription.
            </Typography>
          </>
        )}
      </Box>

      {/* Invite Member Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Team Member</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Invite a new team member to your organization. Seats are claimed on a first-come, first-serve basis when the invited user signs in and accepts the invitation.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            disabled={inviteLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)} disabled={inviteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleInviteMember} 
            variant="contained"
            disabled={inviteLoading || !inviteEmail}
          >
            {inviteLoading ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};